import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
    Box, Button, CircularProgress, IconButton, Link, Menu, MenuItem, Typography,
} from '@mui/material';
import { DataGrid, GridColumnHeaderSortIcon } from '@mui/x-data-grid';

import { useSelector } from 'BMapsRedux';
import { JobTypes, lookupJobDefinition, retrieveJobFile } from 'BMapsSrc/jobman';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { GridToolBar } from 'BMapsUI/tables/toolbars/CustomToolbars';
import {
    ScreeningCoralMap, sortScreeningProperties,
} from 'BMapsUI/tables/graphs/CoralComponents';
import { hexToHsl } from 'BMapsSrc/util/color_utils';
import { applyColumnDirectionality, getFormattedValue } from 'BMapsUI/tables/BMapsTable';
import { CustomColumnsPanel } from 'BMapsUI/tables/CustomColumnsPanel';
import { setActiveChart, setScreeningTableState } from 'BMapsRedux/projectState/access';
import { showAlert } from 'BMapsSrc/utils';
import { Loader } from 'BMapsSrc/Loader';
import { conditionalArrayItem } from 'BMapsSrc/util/js_utils';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { FoldingDataImportCase } from 'BMapsSrc/model';
import { loadProtein } from 'BMapsSrc/project_data';
import { downloadData } from 'BMapsUI/ui_utils';
import { EditCompoundsButton } from 'BMapsUI/tables/toolbars/ToolbarComponents';
import { dataInfoTooltip } from '../../ImportPane/ImportCompoundPane/Tabs/JobsResultsTab';
import { openScreeningSidePanel } from '../ScreeningSidePanel';

export default function ScreeningResults({ selected, activeChart }) {
    const { localJobState, displayedScreeningJobs } = useSelector((state) => state.jobs);

    const results = useMemo(
        () => displayedScreeningJobs.flatMap((job) => localJobState[job.jobid]?.results || []),
        [localJobState, displayedScreeningJobs]
    );

    if (!selected) return null;

    let message; // content when no table to display
    if (!displayedScreeningJobs?.length) {
        message = (
            <span>
                No screening results to display.
                <br />
                Add results from the
                {' '}
                <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                    component="button"
                    onClick={() => openScreeningSidePanel('screen_jobs_tab')}
                    sx={{ color: 'var(--marketing-blue)' }}
                >
                    Screening Jobs Tab
                </Link>
                {' '}
                to view them here.
            </span>
        );
    } else if (!results?.length) {
        message = (
            <span>
                {`The selected screening ${displayedScreeningJobs.length > 1 ? 'jobs' : 'job'} `}
                produced no results.
            </span>
        );
    }
    return (
        <DialogContentSection
            {...scoringTitleProps(displayedScreeningJobs)}
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            disablePaddingTop
        >
            <Typography
                component="div"
                sx={{
                    display: 'flex', flexDirection: 'column', height: '100%',
                }}
            >
                {message || (
                    <ScreeningResultsTable
                        results={results}
                        activeChart={activeChart}
                        displayedScreeningJobs={displayedScreeningJobs}
                    />
                )}
            </Typography>

        </DialogContentSection>
    );
}

/**
 * A screening job's display name - however it's shown throughout this tab (the title
 * bar's job list, and the downloaded structures.zip's filename): the user-given name if
 * one was set, otherwise a date-based fallback that's still unique enough to tell two
 * unnamed jobs apart (unlike, say, today's date, which is the same regardless of which
 * job is being named).
 * @param {object} job
 * @param {boolean} [quote]
 * @returns {string}
 */
export function screeningJobName(job, quote) {
    const name = job.requestmeta?.jobName || `Screen started ${new Date(job.created).toLocaleString('en-US', {
        dateStyle: 'short',
        timeStyle: 'short',
    })}`;
    return quote ? `"${name}"` : name;
}

function scoringTitleProps(displayedScreeningJobs) {
    const title = 'Screening Results';
    if (!displayedScreeningJobs || displayedScreeningJobs.length <= 0) {
        return { title };
    }

    let resultName = screeningJobName(displayedScreeningJobs[0], true);
    if (displayedScreeningJobs.length > 1) {
        resultName += ` and ${displayedScreeningJobs.length - 1} more`;
    }
    const activeJobsDesc = (
        <BMapsTooltip
            title={(
                <div>
                    Loaded Screening Results:
                    <ul style={{ paddingLeft: '1.2em', margin: 0 }}>
                        {displayedScreeningJobs.map((job, i) => (
                            <li key={`${i.toString()}`}>{screeningJobName(job)}</li>
                        ))}
                    </ul>
                </div>
            )}
        >
            {resultName}
        </BMapsTooltip>
    );

    return {
        title: (
            <div>
                {title.toUpperCase()}
                {' '}
                &ndash;
                {' '}
                {activeJobsDesc}
            </div>
        ),
        titleUpperCase: false,
    };
}

/**
 * Active compounds with a retained structure for `targetId` - candidates the Load button
 * can fetch and load. Not filtered by whether any of them happen to already be loaded:
 * this button always just adds a fresh copy to the workspace (see loadTargetStructures),
 * it isn't a tracked load/unload toggle.
 *
 * Must mirror loadTargetStructures' own candidate filter exactly (jobid && archive &&
 * structures[targetId], not just structures[targetId] alone) - jobid/archive are what
 * the actual fetch needs to build a retrieve-job-file URL, and a compound can have a
 * populated structures manifest without them (e.g. a job delivered by an older backend
 * that predates the archive field being added to the wire payload - confirmed against a
 * real one, matt-job-11628). Checking structures alone here would offer a Load button
 * that renders enabled but silently does nothing when clicked, since
 * loadTargetStructures' own stricter filter would find no real candidates.
 * @param {string} targetId
 * @param {ScreeningCompoundInfo[]} activeCompounds compounds to consider - already
 *   filtered to whichever ones should count as "active" by the caller
 */
export function getLoadableCompoundsForTarget(targetId, activeCompounds) {
    return activeCompounds.filter((c) => c.jobid && c.archive && c.structures?.[targetId]);
}

/**
 * Per-row actions menu for the results table - currently just "Add result(s) to 3D
 * workspace". A 3-dot menu rather than a dedicated always-visible icon button,
 * deliberately: this is a repeatable command, not a piece of tracked state. It always
 * just adds a fresh copy to the workspace (loadTargetStructures never checks what's
 * already loaded, keepExisting every time) - a dedicated icon button reads as a toggle
 * ("is this loaded?"), implying tracking that doesn't exist. A menu also leaves room to
 * add more row actions later without adding another dedicated column each time (see the
 * sticky-column history in this table - not worth repeating per action).
 * @param {{
 *   row: object,
 *   onLoad: (targetId: string) => void,
 *   loadingTarget: string?,
 *   getLoadableCompounds: (targetId: string) => ScreeningCompoundInfo[],
 * }} props
 */
function RowActionsMenu({
    row, onLoad, loadingTarget, getLoadableCompounds,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const loadable = getLoadableCompounds(row.target);
    if (loadable.length === 0) return null;

    const isLoading = loadingTarget === row.target;

    return (
        <>
            <IconButton
                size="small"
                aria-label={`Actions for ${row.target}`}
                // Disabled while ANY target is loading, not just this row's -
                // loadTargetStructures' sequential-alignment ordering and the
                // EventBroker.withIgnoredEvents('escapeKey', ...) wrapper around
                // it are both only correct for one in-flight batch at a time.
                // withIgnoredEvents isn't reference-counted (a plain Map
                // set/delete, not a counter - see eventbroker.js), so two
                // overlapping batches would have the first one to finish
                // re-enable escapeKey handling globally while the second is
                // still running, exposing it to the exact side-panel-closing
                // bug that wrapper exists to prevent.
                disabled={loadingTarget !== null}
                onClick={(event) => setAnchorEl(event.currentTarget)}
            >
                {isLoading
                    ? <CircularProgress size={18} />
                    : <MoreVertIcon fontSize="small" />}
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem
                    onClick={() => {
                        setAnchorEl(null);
                        onLoad(row.target);
                    }}
                >
                    {`Add result${loadable.length > 1 ? 's' : ''} to 3D workspace`}
                </MenuItem>
            </Menu>
        </>
    );
}

/**
 * Low-effort pass at populating compound properties for ligands from screening folding,
 * starting with Boltz properties only. bfd-server energies and gnina scores are calculated
 * on the server, not transmitted, but can easily be recalculated client-side.
 *
 * Extracts the folding-program-relevant subset (confidence/affinity) out of a
 * candidate's full scoring properties (compound.properties[targetId] - a flat bag
 * mixing bfd__*, gnina__*, and boltz_*-prefixed CSV columns together, not a
 * folding-only object), and reshapes that subset into the nested {confidence, affinity}
 * shape applyFoldingResultProperties (ConnectedDataCmds.js) expects for
 * FoldingDataImportCase.resultProperties.
 *
 * Only recognizes the boltz_confidence__/boltz_affinity__ prefix today, since Boltz is
 * the only folding program screening currently wires up (ScreeningLogic.logic_defs,
 * screening_helpers.py) - deliberately named without "Boltz" so a future folding
 * program's own prefix can be added alongside it here rather than needing a rename.
 * Generic prefix-stripping for that convention, not a hand-picked list of sub-keys:
 * get_affinity_data (boltz_impl.py) - the same function a standalone fold's
 * resultProperties.affinity already comes from - returns the whole affinity dict
 * verbatim, including derived fields (e.g. affinity_derived_ic50) beyond just the two
 * this file used to single out. Stripping the prefix generically keeps this faithful to
 * whatever that shape actually contains, standalone or screening, without needing to be
 * kept in sync by hand if it changes. bfd- and gnina-prefixed keys are deliberately left
 * out of the result - those get freshly recomputed once the structure is in the
 * workspace, not carried over from screening's own scoring pass.
 * @param {{ [key: string]: number }} [allProperties] compound.properties[targetId] -
 *   every scoring property for this candidate, not just the folding program's.
 */
export function extractResultProperties(allProperties) {
    if (!allProperties) return undefined;
    const resultProperties = {};
    for (const [key, value] of Object.entries(allProperties)) {
        for (const [prefix, category] of [
            ['boltz_confidence__', 'confidence'], ['boltz_affinity__', 'affinity'],
        ]) {
            if (key.startsWith(prefix)) {
                resultProperties[category] = resultProperties[category] || {};
                resultProperties[category][key.slice(prefix.length)] = value;
            }
        }
    }
    return Object.keys(resultProperties).length > 0 ? resultProperties : undefined;
}

/**
 * Load a specific set of compounds' structures for one kinase target into
 * the workspace, each as its own independent protein (Boltz co-folds
 * protein and ligand together, so each (compound, target) structure is its
 * own independently-sampled conformation - there's no single shared
 * structure to merge them onto). Reuses the same multi-protein
 * infrastructure the Project Targets tab's kinase-selectivity comparison
 * already uses: keepExisting loads plus the automatic USAlign-based
 * superposition already built into ChooseProtein.
 * @param {string} targetId
 * @param {ScreeningCompoundInfo[]} compoundsToLoad
 * @param {ScreeningCompoundInfo[]} screeningCompoundInfoList full list, for numbered naming
 */
async function loadTargetStructures(
    targetId, compoundsToLoad, screeningCompoundInfoList
) {
    const candidates = compoundsToLoad.filter(
        (c) => c.jobid && c.archive && c.structures?.[targetId]
    );
    if (candidates.length === 0) return;

    // Fetch bytes for every candidate in parallel - independent network
    // round trips - but the loads themselves happen sequentially below,
    // since each load's alignment depends on what's already in the
    // workspace from the previous one.
    const fetched = await Promise.all(candidates.map(async (compound) => {
        const manifest = compound.structures[targetId];
        const cmpdLabel = getNumberedCompoundName(compound, screeningCompoundInfoList);
        try {
            const arrayBuffer = await retrieveJobFile(
                compound.jobid, { name: compound.archive, member: manifest.filename }
            );
            const data = new TextDecoder('utf-8').decode(arrayBuffer);
            const resultProperties = extractResultProperties(compound.properties?.[targetId]);
            return {
                cmpdLabel, manifest, data, resultProperties,
            };
        } catch (errorCaught) {
            console.warn(`Failed to fetch structure for ${targetId} / ${compound.jobid}:`, errorCaught);
            return { cmpdLabel, error: true };
        }
    }));

    const failures = [];
    for (const {
        cmpdLabel, manifest, data, resultProperties, error,
    } of fetched) {
        if (error) {
            failures.push(`${targetId} (${cmpdLabel}): failed to fetch structure data`);
            continue; // eslint-disable-line no-continue
        }
        // Whether alignment is even possible before this load, so a
        // still-missing reference afterward (when one was expected) can be
        // told apart from "this was the first protein loaded, no reference
        // exists yet" - which is normal, not a failure to surface.
        const refBefore = App.Workspace.defaultAlignmentReferenceCaseData();
        const mapCase = new FoldingDataImportCase({
            data,
            format: manifest.format,
            // Minimal folding data for downstream awareness of the folding program specifics.
            // Ideally populated by the job manager.
            foldingData: { foldingProgramId: 'Boltz-2' },
            // targetId is used for special handling of identifiers downstream.
            // It may get migrated somewhere else later.
            targetId,
            // Boltz confidence/affinity aren't lost, just delivered as flat, prefixed
            // CSV columns rather than this nested shape - see extractResultProperties.
            // Without this, applyFoldingResultProperties (ConnectedDataCmds.js) always
            // no-ops for a screening load, since it reads exactly this field.
            resultProperties,
        });
        let result;
        try {
            // eslint-disable-next-line no-await-in-loop
            result = await loadProtein({
                mapCase,
                loadOptions: { keepExisting: true },
            });
        } catch (errorCaught) {
            // loadProtein isn't guaranteed to only ever resolve - ChooseProtein does
            // real network/DOM work underneath it and can reject. Caught here the same
            // way each fetch's own rejection already is, so one candidate's load failure
            // can't abort every remaining candidate in this loop or skip the aggregate
            // alert below entirely.
            console.warn(`Failed to load structure for ${targetId} / ${cmpdLabel}:`, errorCaught);
            failures.push(`${targetId} (${cmpdLabel}): ${errorCaught?.message || 'load failed'}`);
            continue; // eslint-disable-line no-continue
        }
        if (!result || result.errors?.length > 0) {
            failures.push(`${targetId} (${cmpdLabel}): ${result?.errors?.join('; ') || 'load failed'}`);
        } else if (refBefore && !result.caseData?.getReferenceCaseData()) {
            failures.push(`${targetId} (${cmpdLabel}): loaded, but could not be aligned to the other loaded structure(s)`);
        }
    }
    if (failures.length > 0) {
        showAlert(
            'Load into Workspace',
            ['Some structures did not load cleanly:', ...failures.map((f) => `* ${f}`)]
        );
    }
}

function ScreeningResultsTable({
    results, activeChart, displayedScreeningJobs,
}) {
    const tableState = useSelector((state) => state.projectState.tableState.screeningTable);
    const columnWidths = tableState?.columnWidths || {};
    const [loadingTarget, setLoadingTarget] = useState(null);

    // For the zip-download filename (see downloadStructuresZip below) - a
    // merged view can span several jobs, so this is a lookup, not a single name.
    const jobNamesById = Object.fromEntries(
        displayedScreeningJobs.map((job) => [job.jobid, screeningJobName(job)])
    );

    const setColumnWidths = (widths) => {
        setScreeningTableState({
            columnWidths: widths,
        });
    };

    const screeningCompoundInfoList = tableState?.screeningCompoundInfoList
        ?.map((c) => new ScreeningCompoundInfo(c)) || [];

    /**
     * @param {ScreeningCompoundInfo[]} newScreeningCompoundInfo
     */
    const updateScreeningCompoundInfoState = (newScreeningCompoundInfo) => {
        const screeningCompoundInfoState = newScreeningCompoundInfo
            .map((c) => c.getSavedState());
        setScreeningTableState({
            screeningCompoundInfoList: screeningCompoundInfoState,
        });

        // update the column visibility model
        const columnVisibilityModel = tableState?.columnVisibilityModel;
        if (columnVisibilityModel) {
            const cmpdNames = newScreeningCompoundInfo
                .filter((c) => c.active)
                .map((c) => getNumberedCompoundName(c, newScreeningCompoundInfo));
            // Get the current property visibility from the column visibility model,
            // which has columns named as "<property> <compound>"
            const perPropVisibility = {};
            for (const [key, value] of Object.entries(columnVisibilityModel)) {
                const prop = key.split(' ')[0];
                if (perPropVisibility[prop] == null) {
                    perPropVisibility[prop] = value;
                }
            }
            // Now build a new visibility model for columns for all properties
            // against the active compounds.
            const newVisibilityModel = {};
            for (const [key, value] of Object.entries(perPropVisibility)) {
                for (const cmpdName of cmpdNames) {
                    newVisibilityModel[`${key} ${cmpdName}`] = value;
                }
            }
            setScreeningTableState({
                columnVisibilityModel: newVisibilityModel,
            });
        }
    };

    useEffect(() => {
        if (!results || results.length === 0) return;
        const colors = ['#ff0000', '#377eb8', '#4daf4a'];
        const compoundMap = new Map();
        const overlappingCompounds = [];
        const newAvailableScreeningCompoundInfoList = results.map((inputData, i) => {
            let screeningCompoundInfo = null;
            const alreadyAvailableScreeningCompoundInfo = screeningCompoundInfoList.find(
                (c) => c.smiles === inputData.structure
            );
            if (alreadyAvailableScreeningCompoundInfo) {
                screeningCompoundInfo = alreadyAvailableScreeningCompoundInfo;
            } else {
                screeningCompoundInfo = new ScreeningCompoundInfo({
                    smiles: inputData.structure,
                    name: inputData.name,
                    baseColor: colors[i % colors.length],
                    active: false,
                });
            }
            if (!compoundMap.has(screeningCompoundInfo.smiles)) {
                compoundMap.set(screeningCompoundInfo.smiles, {
                    properties: inputData.properties,
                    structures: inputData.structures,
                    archive: inputData.archive,
                    jobid: inputData.jobid,
                });
            } else {
                if (!overlappingCompounds.includes(screeningCompoundInfo.smiles)) {
                    overlappingCompounds.push(screeningCompoundInfo.smiles);
                }
            }
            return screeningCompoundInfo;
        });
        if (overlappingCompounds.length > 0) {
            showAlert(
                'Duplicate Screening Compounds',
                [
                    'You have loaded screening jobs with overlapping compounds.',
                    'Compounds associated with the following SMILES will show screening properties for only one target structure:',
                    ...overlappingCompounds.map((c) => `* ${c}`),
                ]
            );
        }
        const uniqueScreeningCompoundInfo = _.uniqBy(newAvailableScreeningCompoundInfoList, 'smiles');
        // Initial active compound is the first unique compound
        if (uniqueScreeningCompoundInfo.filter((c) => c.active).length === 0
            && uniqueScreeningCompoundInfo.length > 0) {
            uniqueScreeningCompoundInfo[0].active = true;
        }
        for (const screeningCompoundInfo of uniqueScreeningCompoundInfo) {
            const mapEntry = compoundMap.get(screeningCompoundInfo.smiles);
            screeningCompoundInfo.properties = mapEntry.properties;
            screeningCompoundInfo.structures = mapEntry.structures || {};
            screeningCompoundInfo.archive = mapEntry.archive || null;
            screeningCompoundInfo.jobid = mapEntry.jobid || null;
        }
        updateScreeningCompoundInfoState(uniqueScreeningCompoundInfo);
    }, [results]);

    if (!screeningCompoundInfoList || screeningCompoundInfoList.length === 0) return null;

    const availableCharts = [{
        id: 'bmapsTable',
        name: 'Table',
        componentProps: {
            rows: [],
            columns: [],
        },

    },
    {
        id: 'coralMap',
        name: 'Coral Map',
        componentProps: {},
    }];

    function getLoadableCompounds(targetId) {
        const activeCompounds = screeningCompoundInfoList.filter((c) => c.active);
        return getLoadableCompoundsForTarget(targetId, activeCompounds);
    }

    async function handleLoadTarget(targetId) {
        const loadable = getLoadableCompounds(targetId);
        if (loadable.length === 0) return;
        setLoadingTarget(targetId);
        try {
            // Loading a protein can fire an internal 'escapeKey' event (see UIManager's
            // handleZapAll) that a generic dialog/panel-close listener reacts to - ignore it
            // for the duration, same as ImportProteinPane/ProjectTargetsTab already do for
            // their own protein loads, so this panel doesn't close out from under the user.
            await EventBroker.withIgnoredEvents('escapeKey', (async () => {
                await loadTargetStructures(targetId, loadable, screeningCompoundInfoList);
            })());
        } finally {
            setLoadingTarget(null);
        }
    }

    const {
        rows, columns, columnGroupingModel, defaultColumnVisibility,
    } = buildScreeningResultsTable(
        screeningCompoundInfoList,
        columnWidths,
        {
            onLoad: handleLoadTarget,
            loadingTarget,
            getLoadableCompounds,
        }
    );

    function handleColumnWidthChange(params) {
        const newWidths = { ...columnWidths, [params.colDef.field]: params.width };
        setColumnWidths(newWidths);
    }
    return (
        <>
            { activeChart === 'bmapsTable'
                && (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        columnGroupingModel={columnGroupingModel}
                        pageSize={5}
                        rowsPerPageOptions={[5]}
                        rowHeight={60}
                        columnVisibilityModel={
                            tableState?.columnVisibilityModel
                            || defaultColumnVisibility
                        }
                        onColumnVisibilityModelChange={(columnVisibilityModel) => {
                            setScreeningTableState({ columnVisibilityModel });
                        }}
                        onColumnWidthChange={handleColumnWidthChange}
                        showToolbar
                        slotProps={{
                            basePopper: { placement: 'bottom-start' },
                        }}
                        slots={{
                            toolbar: (toolbarProps) => (
                                <GridToolBar
                                    {...toolbarProps}
                                    toolBarId="ScreeningResultsTable"
                                    charts={availableCharts}
                                    setChart={(chart) => setActiveChart(chart, 'screeningTable')}
                                    extraExportOptions={[
                                        ...conditionalArrayItem(
                                            Loader.AllowLabFeatures,
                                            makeStructuresZipOption(
                                                screeningCompoundInfoList, jobNamesById
                                            )
                                        ),
                                    ]}
                                >
                                    <EditCompoundsButton
                                        compoundOptions={screeningCompoundInfoList}
                                        updateCompoundInfo={updateScreeningCompoundInfoState}
                                    />
                                </GridToolBar>
                            ),
                            columnsPanel: CustomColumnsPanel,
                            columnHeaderSortIcon: (props) => {
                                const found = columns.find((c) => c.field === props.field);
                                const title = found ? `Sort by ${found.headerName}` : 'Sort';
                                return (
                                    <BMapsTooltip title={title} noMaxWidth>
                                        <GridColumnHeaderSortIcon {...props} />
                                    </BMapsTooltip>
                                );
                            },
                        }}
                        sx={{
                            '& .compound-header': {
                                backgroundColor: 'lightGray',
                                borderRight: '1px solid white',
                                borderLeft: '1px solid white',
                            },
                            '& .MuiDataGrid-columnHeader--emptyGroup': {
                                borderBottom: '1px solid lightgray',
                                '& .MuiDataGrid-columnSeparator': {
                                    display: 'none',
                                },
                            },
                            '& .MuiTablePagination-root': {
                                overflow: 'hidden',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                minHeight: '3rem',
                            },
                        }}
                    />
                )}
            { activeChart === 'coralMap' && (
            <ScreeningCoralMap
                activeScreeningCompoundInfoList={
                                        screeningCompoundInfoList.filter((c) => c.active)
                                    }
                availableCharts={availableCharts}
                setChart={(chart) => setActiveChart(chart, 'screeningTable')}
                availableScreeningCompoundInfoList={screeningCompoundInfoList}
                updateScreeningCompoundInfo={updateScreeningCompoundInfoState}
                jobNamesById={jobNamesById}
            />
            )}
        </>
    );
}

/**
 * Collects the unique set of (jobid, archive filename) pairs that
 * contributed at least one retained 3D structure across the given
 * compounds. archive/jobid are compound-level fields (one job per compound
 * - ScreeningResultsTable's SMILES-dedup merge keeps the first job's data
 * rather than stitching targets together across jobs), but different
 * compounds in the list can still come from different jobs once results
 * from several screens are merged in the same view - hence still a set,
 * not a single pair. The archive filename is read from the compound
 * itself - the backend (screening_activity.py's STRUCTURES_ZIP_NAME) is
 * the one source of truth for that name, so the frontend never hardcodes
 * its own copy that could drift out of sync.
 * @param {ScreeningCompoundInfo[]} screeningCompoundInfoList
 * @returns {{ jobid: string, archive: string }[]}
 */
export function getStructureArchiveTargets(screeningCompoundInfoList) {
    const targets = new Map(); // jobid -> archive filename
    for (const info of screeningCompoundInfoList || []) {
        // archive/jobid are only ever set when the compound has at least one
        // retained structure (see LibraryScreeningJob.integrateResults), so
        // this check alone is sufficient - no need to also inspect structures.
        if (info.jobid && info.archive) {
            targets.set(info.jobid, info.archive);
        }
    }
    return [...targets.entries()].map(([jobid, archive]) => ({ jobid, archive }));
}

/** Cap on the sanitized job-name portion of a download filename (see sanitizeForFilename). */
const MAX_FILENAME_BASE = 100;

/**
 * Filesystem-unsafe characters stripped from a job's display name before using it in a
 * downloaded filename - the name is free text a user typed (or a "Screen started <date>"
 * fallback, see screeningJobName), never validated against what's safe in a filename.
 *
 * Length-bounded for the same reason: most filesystems cap a single path component at 255
 * bytes, and nothing stops a user naming a job with a paragraph. Truncating here fails
 * cheaply and predictably, rather than letting the OS reject or silently mangle the save
 * after the archive has already been fetched.
 * @param {string} name
 * @returns {string}
 */
function sanitizeForFilename(name) {
    return name
        .replace(/[/\\:*?"<>|]/g, '_')
        .replace(/\s+/g, '_')
        .slice(0, MAX_FILENAME_BASE);
}

/**
 * Fetches a screening job's structures archive and triggers a browser
 * download of it. Uses JobsApi.retrieveJobFile's whole-file mode - the same
 * primitive the table/coral-map load-into-workspace fetch uses with an
 * added `member` to pull just one structure instead of the whole archive.
 * @param {string} jobid
 * @param {string} archive filename, as given by the manifest's "archive" field
 * @param {string} [jobName] the job's display name (screeningJobName) - falls back to
 *   the bare jobid if not given, since that's still a valid, if not very meaningful,
 *   filename.
 */
export async function downloadStructuresZip(jobid, archive, jobName) {
    const arrayBuffer = await retrieveJobFile(jobid, { name: archive });
    const base = jobName ? sanitizeForFilename(jobName) : jobid;
    downloadData(`${base}_${archive}`, arrayBuffer, 'application/zip');
}

/**
 * Body content for the multi-job download dialog (see makeStructuresZipOption) - one
 * button per job, each its own genuine user gesture. Deliberately NOT an automatic
 * loop: browsers gate multiple automatic downloads triggered from a single click
 * (Chrome shows a "download multiple files?" prompt and silently drops anything past
 * the first if the user doesn't approve it, with no error surfaced to the page at all -
 * a rejected fetch is catchable, a browser-blocked save is not). Clicking each of these
 * buttons individually is a separate gesture the browser never throttles.
 *
 * showAlert's dialog only closes on its own "OK" button (rendered outside this body by
 * DialogProvider), so clicking a download here doesn't dismiss the dialog - the user can
 * work through every job before closing it.
 * @param {{
 *   targets: {jobid: string, archive: string}[],
 *   jobNamesById?: { [jobid: string]: string },
 * }} props
 */
function MultiJobDownloadList({ targets, jobNamesById }) {
    const [statusByJob, setStatusByJob] = useState({});

    async function handleClick(jobid, archive) {
        setStatusByJob((s) => ({ ...s, [jobid]: 'downloading' }));
        try {
            await downloadStructuresZip(jobid, archive, jobNamesById?.[jobid]);
            setStatusByJob((s) => ({ ...s, [jobid]: 'done' }));
        } catch (errorCaught) {
            console.warn(`Error downloading 3D structures for job ${jobid}:`, errorCaught);
            setStatusByJob((s) => ({ ...s, [jobid]: 'error' }));
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Typography sx={{ marginBottom: '0.5rem' }}>
                {`This view spans ${targets.length} jobs with retained structures. Download each archive individually:`}
            </Typography>
            {targets.map(({ jobid, archive }) => {
                const status = statusByJob[jobid] || 'idle';
                const label = jobNamesById?.[jobid] || jobid;
                return (
                    <Box key={jobid} sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={status === 'downloading' || status === 'done'}
                            onClick={() => handleClick(jobid, archive)}
                            sx={{ minWidth: '10rem' }}
                        >
                            {status === 'done' && 'Downloaded'}
                            {status === 'downloading' && 'Downloading…'}
                            {(status === 'idle' || status === 'error') && `Download: ${label}`}
                        </Button>
                        {status === 'error' && (
                            <Typography color="error.main" variant="body2">
                                Failed - click to try again
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

/**
 * Builds the "Download 3D data (.zip)" export option, or null when there are
 * no retained structures to offer (or this isn't a screening table at all).
 * Shared between the Coral map export menu and the DataGrid export menu.
 * @param {ScreeningCompoundInfo[]} availableScreeningCompoundInfoList
 * @param {{ [jobid: string]: string }} [jobNamesById] for naming the downloaded file
 *   after the job (see screeningJobName) rather than its internal, not-very-user-facing
 *   jobid - a merged view can span several jobs, hence a lookup rather than one name.
 * @returns {{title: string, disabled: boolean, handleExport: () => Promise<void>} | null}
 */
export function makeStructuresZipOption(availableScreeningCompoundInfoList, jobNamesById) {
    if (!availableScreeningCompoundInfoList) return null;
    const targets = getStructureArchiveTargets(availableScreeningCompoundInfoList);
    return {
        title: 'Download 3D data (.zip)',
        disabled: targets.length === 0,
        handleExport: async () => {
            // More than one job's archive: never auto-loop the downloads (see
            // MultiJobDownloadList's doc for why) - hand the user a picker instead.
            if (targets.length > 1) {
                await showAlert(
                    'Download 3D Data',
                    <MultiJobDownloadList targets={targets} jobNamesById={jobNamesById} />
                );
                return;
            }
            const failures = [];
            for (const { jobid, archive } of targets) {
                const jobLabel = jobNamesById?.[jobid] || jobid;
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await downloadStructuresZip(jobid, archive, jobNamesById?.[jobid]);
                } catch (errorCaught) {
                    console.warn(`Error downloading 3D structures for job ${jobid}:`, errorCaught);
                    failures.push(`${jobLabel}: ${errorCaught?.message || 'download failed'}`);
                }
            }
            // A failed fetch here has nothing else to signal it - unlike a normal browser
            // download, there's no separate progress/error UI to fall back on, so a
            // swallowed rejection here was previously indistinguishable from a no-op
            // click. Same aggregate-alert treatment as loadTargetStructures.
            if (failures.length > 0) {
                showAlert(
                    'Download 3D Data',
                    ['Some archives could not be downloaded:', ...failures.map((f) => `* ${f}`)]
                );
            }
        },
    };
}

function makeColumnKey(prop, compound, allCompounds) {
    const headerName = getNumberedCompoundName(compound, allCompounds);
    if (!compound) {
        return prop;
    }
    return `${prop} ${headerName}`;
}

/**
 * Unnamed compounds are named "Compound X" where X is their position in the list of compounds.
 * Named compounds that appear multiple times are named "Name (Y)" where Y is their position
 * among compounds with the same name.
 * @param {ScreeningCompoundInfo} screeningCompoundInfo
 * @param {ScreeningCompoundInfo[]} screeningCompoundInfoList
 * @returns {string}
 */
export function getNumberedCompoundName(screeningCompoundInfo, screeningCompoundInfoList) {
    const defaultName = 'Compound';
    const name = screeningCompoundInfo.name;
    if (!name || name.trim() === '') {
        // For unnamed compounds, use position in list of available compounds
        const index = screeningCompoundInfoList.indexOf(screeningCompoundInfo);
        return `${defaultName} ${index + 1}`;
    }

    const sameCompounds = screeningCompoundInfoList.filter((c) => c.name === name);
    if (sameCompounds.length > 1) {
        // Append position among compounds with the same name
        const compoundIndex = sameCompounds.indexOf(screeningCompoundInfo);
        return `${name} (${compoundIndex + 1})`;
    }
    return name;
}

function buildScreeningResultsTable(
    screeningCompoundInfoList,
    columnWidths,
    {
        onLoad, loadingTarget, getLoadableCompounds,
    }
) {
    const activeScreeningCompoundInfoList = screeningCompoundInfoList
        .filter((c) => c.active);

    if (!activeScreeningCompoundInfoList || activeScreeningCompoundInfoList.length === 0) {
        return {
            rows: [],
            columns: [],
            columnGroupingModel: [],
            defaultColumnVisibility: {},
        };
    }
    const columns = [{
        field: 'target',
        headerName: 'Target',
        title: 'Target',
        width: columnWidths.target,
        flex: columnWidths.target ? undefined : 1,
    }];
    const rows = [];
    const propertyList = [];
    const propInfos = lookupJobDefinition(JobTypes.LIBRARY_SCREEN).dataInfo;
    const defaultColumnVisibility = {};

    for (const activeScreeningCompoundInfo of activeScreeningCompoundInfoList) {
        const properties = activeScreeningCompoundInfo.properties;
        const cmpdLabel = getNumberedCompoundName(
            activeScreeningCompoundInfo, screeningCompoundInfoList
        );
        for (const [targetId, propsAgainstTarget] of Object.entries(properties)) {
            const propValues = {};
            const propsAgainstTargetList = Object.keys(propsAgainstTarget).map((p) => ({
                property: p,
                propertyValue: propsAgainstTarget[p],
            }));

            // Sort columns to match the order in propInfos
            sortScreeningProperties(propsAgainstTargetList, (c) => c.property);

            for (const { property, propertyValue } of propsAgainstTargetList) {
                const propInfo = propInfos[property] || {};
                const propertyColKey = makeColumnKey(
                    property, activeScreeningCompoundInfo, screeningCompoundInfoList
                );
                const propLabel = propInfo.label || property;
                const labelWithCmpd = `${propLabel} ${cmpdLabel}`;
                if (!columns.find((column) => column?.field === propertyColKey)) {
                    const columnInfo = {
                        field: propertyColKey,
                        headerName: labelWithCmpd,
                        title: propLabel,
                        description: dataInfoTooltip(propInfos[property]),
                        width: columnWidths[propertyColKey],
                        flex: columnWidths[propertyColKey] ? undefined : 1,
                        type: propInfo.type || 'number',
                        renderHeader: () => (
                            <BMapsTooltip title={`${propLabel} for ${cmpdLabel}`} noMaxWidth>
                                <span>{propLabel}</span>
                            </BMapsTooltip>
                        ),
                        renderCell: ({ value: propValue }) => (
                            <BMapsTooltip title={propValue} placement="bottom-start">
                                <span>{getFormattedValue(propValue, propInfo.precision)}</span>
                            </BMapsTooltip>
                        ),
                    };
                    applyColumnDirectionality(columnInfo, propInfo.directionality);
                    columns.push(columnInfo);
                }
                propValues[propertyColKey] = propertyValue;
                if (!propertyList.includes(property)) propertyList.push(property);
                if (!propInfo.defaultVisible) defaultColumnVisibility[propertyColKey] = false;
            }
            const existingRowIndex = rows.findIndex((row) => row.target === targetId);
            if (existingRowIndex !== -1) {
                rows[existingRowIndex] = {
                    ...rows[existingRowIndex],
                    ...propValues,
                };
            } else {
                rows.push({
                    id: targetId,
                    target: targetId,
                    ...propValues,
                });
            }
        }
    }

    const columnGroupingModel = [
        ...activeScreeningCompoundInfoList.map((compound) => {
            const headerName = getNumberedCompoundName(compound, screeningCompoundInfoList);
            return {
                groupId: headerName,
                headerName,
                headerAlign: 'center',
                headerClassName: 'compound-header',
                children: propertyList.map(
                    (key) => ({ field: makeColumnKey(key, compound, screeningCompoundInfoList) })
                ),
            };
        }),
    ];

    // Appended last, and NOT sticky/pinned - accepted tradeoff, not an oversight. True
    // column pinning is MUI X Pro-only (Community forces the pinning state back to empty),
    // and every hand-rolled CSS/virtualization workaround tried here to fake it left a
    // real, visible gap (the column vanishing during active horizontal scroll) that isn't
    // acceptable to ship. A leading position was tried too and rejected on its own
    // merits, independent of the pinning problem. So: this column sits at the end like any
    // other, and with enough property columns loaded, reaching it requires scrolling like
    // everything else in the table - a known, deliberate limitation, not a bug. Left open
    // whether a real MUI X Pro license is worth it later for reliable pinned action
    // columns (this table isn't the only one that could use one).
    if (activeScreeningCompoundInfoList.some((c) => c.hasAnyStructure())) {
        columns.push({
            field: 'loadIntoWorkspace',
            headerName: '',
            title: '',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            hideable: false,
            width: 48,
            renderCell: ({ row }) => (
                <RowActionsMenu
                    row={row}
                    onLoad={onLoad}
                    loadingTarget={loadingTarget}
                    getLoadableCompounds={getLoadableCompounds}
                />
            ),
        });
    }

    return {
        rows, columns, columnGroupingModel, defaultColumnVisibility,
    };
}

/**
 * @typedef {{
 * [kinaseOrGroup: string]: {
 * branchColor: string, nodeSize: number, tooltip: JSX.Element
 * },
 * branchColor: string, label: {textColor: string}, nodeSize: number, tooltip: JSX.Element
 * }} CoralStyle
 */

/**
 * @typedef {{
 * structure: string,
 * properties: {
 * [kinase: string]: {
 * [property: string]: number
 * }
 * }
 * }[]} inputData
 */

/**
 * @typedef {{ smiles: string, baseHue: number }} coralCompound
 */

export class ScreeningCompoundInfo {
    constructor({
        smiles, name='', properties={}, structures={}, archive=null, jobid=null,
        baseColor='#ff0000', active=false,
    }) {
        /** @type {string} */
        this.smiles = smiles;
        /** @type {string} */
        this.baseColor = baseColor;
        /** @type {{ [kinase: string]: { [property: string]: number } }} */
        this.properties = properties;
        /**
         * Manifest only ({filename, format}, no raw structure data) for
         * whichever (kinase, this compound) pairs have a retained 3D structure.
         * Populated from LibraryScreeningJob.integrateResults.
         * @type {{ [kinase: string]: { filename: string, format: string } }}
         */
        this.structures = structures;
        /**
         * Which job's staged file this compound's structures come from, and
         * the archive's filename within that job (currently always
         * "structures.zip") - compound-level facts, not per-target, since a
         * compound's structures/properties all originate from one job (the
         * SMILES-dedup merge in ScreeningResultsTable keeps the first job's
         * data, it doesn't stitch targets together across jobs). null when
         * this compound has no retained structures at all.
         * @type {string | null}
         */
        this.archive = archive;
        /** @type {string | null} */
        this.jobid = jobid;
        this.name = name;
        this.active = active;
    }

    /**
     * @returns {boolean} true if any kinase for this compound has a structure that can
     *   actually be fetched and loaded - requires jobid/archive in addition to a
     *   non-empty structures manifest, since a compound can have manifest entries
     *   without them (e.g. a job delivered by a backend version predating the archive
     *   field on the wire payload - confirmed against a real one, matt-job-11628).
     *   Matches loadTargetStructures'/getLoadableCompoundsForTarget's own candidate
     *   filter, so the Load column never appears for a compound that would silently
     *   fail to load anything if clicked.
     */
    hasAnyStructure() {
        return !!(this.jobid && this.archive && Object.keys(this.structures).length > 0);
    }

    /**
     * @param {string} property
     * @returns {{ [kinase: string]: number }}
     */
    getKinaseValuesForProperty(property) {
        const kinaseValues = {};
        for (const [kinase, properties] of Object.entries(this.properties)) {
            kinaseValues[kinase] = properties[property];
        }
        return kinaseValues;
    }

    /**
     * @param {string} kinase
     */
    getPropertiesForKinase(kinase) {
        return this.properties[kinase];
    }

    getPropertyValue(kinase, property) {
        return this.properties[kinase][property];
    }

    setProperties(properties) {
        this.properties = properties;
    }

    getPropertyList() {
        const retProperties = [];
        for (const [kinase, properties] of Object.entries(this.properties)) {
            for (const [property, value] of Object.entries(properties)) {
                if (!retProperties.includes(property)) retProperties.push(property);
            }
        }
        return retProperties;
    }

    // getPropertyList() {
    //     const propInfos = lookupJobDefinition(JobTypes.LIBRARY_SCREEN).dataInfo;
    //     const retProperties = [];
    //     for (const [kinase, properties] of Object.entries(this.properties)) {
    //         for (const [key, value] of Object.entries(properties)) {
    //             const propInfo = propInfos[key] || {};
    //             if (!retProperties.find((p) => p.key === key)) {
    //                 retProperties.push({
    //                     key,
    //                     label: propInfo.label || key,
    //                     description: propInfo.description || '',
    //                     unit: propInfo.unit || '',
    //                     directionality: propInfo.directionality || null,
    //                 });
    //             }
    //         }
    //     }
    //     return retProperties;
    // }

    getMaxValue(property, maxThreshold) {
        let maxValue = null;
        for (const [kinase, properties] of Object.entries(this.properties)) {
            const propertyValue = properties[property];
            if (propertyValue == null) continue;
            if (maxThreshold != null && propertyValue > maxThreshold) continue;
            if (maxValue == null || maxValue < propertyValue) maxValue = propertyValue;
        }
        return maxValue;
    }

    getMinValue(property, minThreshold) {
        let minValue = null;
        for (const [kinase, properties] of Object.entries(this.properties)) {
            const propertyValue = properties[property];
            if (propertyValue == null) continue;
            if (minThreshold != null && propertyValue < minThreshold) continue;
            if (minValue == null || minValue > propertyValue) minValue = propertyValue;
        }
        return minValue;
    }

    getBaseHue() {
        const hexNum = parseInt(this.baseColor.replace(/^#/, ''), 16);
        return hexToHsl(hexNum)[0];
    }

    getSavedState() {
        return {
            smiles: this.smiles,
            baseColor: this.baseColor,
            name: this.name,
            active: this.active,
            properties: this.properties,
            structures: this.structures,
            archive: this.archive,
            jobid: this.jobid,
        };
    }
}
